import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, Mail, Phone, Calendar, Eye, User } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/customers`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name?.toLowerCase().includes(search.toLowerCase()) ||
      customer.email?.toLowerCase().includes(search.toLowerCase()) ||
      customer.phone?.includes(search),
  );

  return (
    <div data-testid="admin-customers">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-slate-900">
          Customers
        </h1>
        <p className="text-slate-600">Manage customer information</p>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="pl-10"
              data-testid="customer-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">
          Loading customers...
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          No customers found
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map((customer, index) => (
            <motion.div
              key={customer.email}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="font-heading font-bold text-xl text-primary-600">
                        {customer.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedCustomer(customer)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2">
                    {customer.name}
                  </h3>

                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {customer.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {customer.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {customer.booking_count} booking
                      {customer.booking_count !== 1 ? "s" : ""}
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-sm">
                      <span className="text-slate-500">Total Spent:</span>
                      <span className="font-bold text-primary-600 ml-2">
                        €{customer.total_spent?.toLocaleString()}
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Customer Details Modal */}
      <Dialog
        open={!!selectedCustomer}
        onOpenChange={() => setSelectedCustomer(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Customer Details</DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="font-heading font-bold text-2xl text-primary-600">
                    {selectedCustomer.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-slate-900">
                    {selectedCustomer.name}
                  </h3>
                  <p className="text-slate-500">
                    Customer since {selectedCustomer.first_booking || "N/A"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary-600">
                    {selectedCustomer.booking_count}
                  </p>
                  <p className="text-sm text-slate-500">Total Bookings</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-secondary-600">
                    €{selectedCustomer.total_spent?.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-500">Total Spent</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">
                  Contact Information
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a
                      href={`mailto:${selectedCustomer.email}`}
                      className="text-primary-600 hover:underline"
                    >
                      {selectedCustomer.email}
                    </a>
                  </p>
                  <p className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a
                      href={`tel:${selectedCustomer.phone}`}
                      className="text-primary-600 hover:underline"
                    >
                      {selectedCustomer.phone}
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button className="flex-1 bg-primary-600 hover:bg-primary-700">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button variant="outline" className="flex-1">
                  <Calendar className="w-4 h-4 mr-2" />
                  View Bookings
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
